// frontend/src/components/EnhancedInsuranceIcons/TravelInsuranceIcon.jsx
import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './gsapSetup'; // Import the setup file to register plugins
import styles from './EnhancedInsuranceIcons.module.css';

const TravelInsuranceIcon = ({ size = 120 }) => {
  const travelRef = useRef(null);
  
  useEffect(() => {
    if (!travelRef.current) return;
    
    const globe = travelRef.current.querySelector('#globe');
    const continents = travelRef.current.querySelector('#continents');
    const plane = travelRef.current.querySelector('#plane');
    const planeTrail = travelRef.current.querySelector('#plane-trail');
    const suitcase = travelRef.current.querySelector('#suitcase');
    const suitcaseHandle = travelRef.current.querySelector('#suitcase-handle');
    const passport = travelRef.current.querySelector('#passport');
    const passportSymbol = travelRef.current.querySelector('#passport-symbol');
    
    // Hide all elements initially
    gsap.set([globe, continents, plane, planeTrail, suitcase, suitcaseHandle, passport, passportSymbol], { 
      opacity: 0,
      y: -20
    });
    
    // Create animation timeline
    const tl = gsap.timeline({ defaults: { ease: "back.out(1.7)" } });
    
    // Animate each part dropping in with slight bounce effect
    tl.to(globe, { opacity: 1, y: 0, duration: 0.6, delay: 0.2 })
      .to(continents, { opacity: 1, y: 0, duration: 0.5 }, "-=0.3")
      .to(suitcase, { opacity: 1, y: 0, duration: 0.5 }, "-=0.2")
      .to(suitcaseHandle, { opacity: 1, y: 0, duration: 0.4 }, "-=0.3")
      .to(passport, { opacity: 1, y: 0, duration: 0.4 }, "-=0.2")
      .to(passportSymbol, { opacity: 1, y: 0, duration: 0.3 }, "-=0.2")
      .to(plane, { opacity: 1, y: 0, duration: 0.5 }, "-=0.1")
      .to(planeTrail, { opacity: 0.6, y: 0, duration: 0.4 }, "-=0.3");
    
    // Globe rotation
    gsap.to(globe, {
      rotation: 360,
      transformOrigin: "center center",
      duration: 30,
      repeat: -1,
      ease: "none"
    });
    
    // Plane animation (flying around the globe)
    const planeFlight = gsap.timeline({ repeat: -1 });
    planeFlight.to(plane, {
      motionPath: {
        path: "#plane-path",
        align: "#plane-path",
        alignOrigin: [0.5, 0.5],
        autoRotate: true
      },
      duration: 8,
      ease: "none"
    });
    
    // Plane trail opacity animation
    gsap.to(planeTrail, {
      opacity: 0.2,
      duration: 1.5,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    // Suitcase subtle movement
    gsap.to(suitcase, {
      y: "-=2",
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    // Passport subtle movement
    gsap.to(passport, {
      rotation: 5,
      transformOrigin: "bottom center",
      duration: 2.5,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut"
    });
    
    return () => {
      // Clean up animations when component unmounts
      tl.kill();
      planeFlight.kill();
      gsap.killTweensOf([globe, continents, plane, planeTrail, suitcase, suitcaseHandle, passport, passportSymbol]);
    };
  }, []);
  
  return (
    <div className={styles.iconContainer} ref={travelRef} style={{ width: size, height: size }}>
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
        {/* Globe */}
        <circle id="globe" cx="50" cy="50" r="25" fill="#2348ED" opacity="0.8" filter="url(#shadow)" />
        
        {/* Continents */}
        <g id="continents" fill="#38C793" opacity="0.9">
          <path d="M45,30 Q50,25 55,30 T60,40 Q55,45 50,43 T45,30" />
          <path d="M65,45 Q70,48 68,55 T60,60 Q58,55 60,50 T65,45" />
          <path d="M40,55 Q35,58 35,65 T45,70 Q48,65 45,60 T40,55" />
          <path d="M30,40 Q25,45 30,50 T35,45 Q33,42 32,40 T30,40" />
        </g>
        
        {/* Plane path (invisible) */}
        <path 
          id="plane-path" 
          d="M50,15 C80,20 80,80 50,85 C20,80 20,20 50,15" 
          fill="none" 
          stroke="transparent" 
        />
        
        {/* Airplane */}
        <g id="plane" transform="translate(0, 0) scale(0.8)">
          <path d="M0,0 L8,3 L8,-3 Z" fill="#FFFFFF" filter="url(#shadow)" />
          <rect x="-4" y="-0.5" width="4" height="1" fill="#FFFFFF" />
          <rect x="2" y="-3" width="1" height="6" fill="#FFFFFF" />
        </g>
        
        {/* Plane trail */}
        <path 
          id="plane-trail" 
          d="M55,15 C65,20 70,30 70,40" 
          stroke="#FFFFFF" 
          strokeWidth="1" 
          strokeDasharray="2 3" 
          fill="none" 
          opacity="0.6" 
        />
        
        {/* Suitcase */}
        <g transform="translate(20, 75)">
          <rect id="suitcase" x="-10" y="-8" width="20" height="15" rx="2" fill="#F17B2C" filter="url(#shadow)" />
          <path 
            id="suitcase-handle" 
            d="M-3,-8 C-3,-13 3,-13 3,-8" 
            stroke="#333333" 
            strokeWidth="2" 
            fill="none" 
          />
        </g>
        
        {/* Passport */}
        <g transform="translate(75, 75)">
          <rect id="passport" x="-8" y="-12" width="16" height="20" rx="2" fill="#DF1C41" filter="url(#shadow)" />
          <circle id="passport-symbol" cx="0" cy="-2" r="5" fill="#F2AE40" />
        </g>
        
        {/* Filters */}
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="3" stdDeviation="2" floodOpacity="0.2" />
          </filter>
        </defs>
      </svg>
    </div>
  );
};

export default TravelInsuranceIcon;